/**
 * Input masking functionality using IMask library.
 * Provides pattern, regexp, and number masking with .NET integration.
 */

'use strict';

import IMask from '../imask.js';
import type {
    DotNetObjectReference,
    MaskConfig,
    MaskPayload,
    MaskValueMode,
    MaskInstance,
    MaskHandlers
} from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

/**
 * Registry of mask instances by unique ID.
 */
const maskRegistry = new Map<string, MaskInstance>();

/**
 * Event listener options for passive events.
 */
const PASSIVE_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true };

/**
 * Generate a unique ID for a mask instance.
 * Uses crypto.randomUUID if available, falls back to random string.
 * 
 * @returns Unique identifier
 */
function generateUniqueId(): string {
    try {
        if (typeof crypto !== 'undefined') {
            if (typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
            }
            if (typeof crypto.getRandomValues === 'function') {
                const array = new Uint8Array(16);
                crypto.getRandomValues(array);
                return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
            }
        }
    } catch {
        // Fallback to timestamp-based ID
    }

    return `${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

/**
 * Build IMask options from configuration object.
 * 
 * @param config - Mask configuration
 * @returns IMask options object
 */
function buildMaskOptions(config: MaskConfig): any {
    const options: any = {};
    const kind = config?.kind || 'pattern';

    if (kind === 'regexp' && config?.regex) {
        try {
            options.mask = new RegExp(config.regex);
        } catch {
            options.mask = '';
        }
    } else if (kind === 'number') {
        options.mask = IMask.MaskedNumber;
        options.radix = config?.radix || '.';
        options.thousandsSeparator = config?.thousandsSeparator || '';

        if (typeof config?.min === 'number') options.min = config.min;
        if (typeof config?.max === 'number') options.max = config.max;
        if (typeof config?.scale === 'number') options.scale = config.scale;
        if (typeof config?.normalizeZeros === 'boolean') options.normalizeZeros = config.normalizeZeros;
        if (typeof config?.padFractionalZeros === 'boolean') options.padFractionalZeros = config.padFractionalZeros;
    } else {
        options.mask = config?.mask ?? '';
    }

    if (typeof config?.lazy === 'boolean') options.lazy = config.lazy;
    if (config?.placeholderChar) options.placeholderChar = config.placeholderChar;

    return options;
}

/**
 * Create a mask payload for .NET callbacks.
 * 
 * @param instance - IMask instance
 * @returns Payload with value, unmasked, and typed values
 */
function createMaskPayload(instance: any): MaskPayload {
    try {
        const typed = instance.typedValue;
        return {
            value: instance.value ?? null,
            unmasked: instance.unmaskedValue ?? null,
            typed: typed != null ? (typeof typed === 'string' ? typed : String(typed)) : null
        };
    } catch {
        return { value: null, unmasked: null, typed: null };
    }
}

/**
 * Add event listener with passive option, falling back if not supported.
 */
function addPassiveListener(
    element: HTMLElement,
    event: string,
    handler: (e: Event) => void
): void {
    try {
        element.addEventListener(event, handler, PASSIVE_LISTENER_OPTIONS);
    } catch {
        // Fallback for browsers that don't support options
        element.addEventListener(event, handler);
    }
}

/**
 * Initialize a new IMask instance and register events for .NET interop.
 * 
 * @param element - Input element to apply mask to
 * @param config - Mask configuration
 * @param dotnetHelper - Optional .NET object reference for callbacks
 * @returns Unique instance ID
 * @throws Error if element is null
 */
export function initialize(
    element: HTMLElement | null,
    config: MaskConfig,
    dotnetHelper?: DotNetObjectReference
): string {
    if (!element) {
        throw new Error('Mask initialization failed: element is required');
    }

    // If this element already has a mask, dispose it to avoid duplicates
    const existingId = (element as any).dataset?.imaskId;
    if (existingId && maskRegistry.has(existingId)) {
        try {
            dispose(existingId);
        } catch {
            // Ignore disposal errors
        }
    }

    const options = buildMaskOptions(config || {});
    const instance = IMask(element, options);
    const id = generateUniqueId();

    const handlers: MaskHandlers = {};

    // Attach .NET callbacks if helper provided
    if (dotnetHelper) {
        const fireEvent = (eventName: string) => {
            const payload = createMaskPayload(instance);
            safeInvokeSync(dotnetHelper, eventName, payload);
        };

        handlers.accept = () => fireEvent('OnMaskAccept');
        handlers.complete = () => fireEvent('OnMaskComplete');
        handlers.blur = () => fireEvent('OnMaskBlur');

        instance.on('accept', handlers.accept);
        instance.on('complete', handlers.complete);
        addPassiveListener(element, 'blur', handlers.blur);
    }

    maskRegistry.set(id, { inst: instance, element, handlers, dotnet: dotnetHelper });

    // Store ID on element for cleanup tracking
    try {
        (element as any).dataset.imaskId = id;
    } catch {
        // Ignore if dataset is not available
    }

    return id;
}

/**
 * Dispose of a mask instance and detach all handlers.
 * 
 * @param id - Instance ID
 * @returns True if instance was found and disposed
 */
export function dispose(id: string): boolean {
    const entry = maskRegistry.get(id);
    if (!entry) {
        return false;
    }

    const { inst, element, handlers } = entry;

    try {
        // Remove IMask event handlers
        if (handlers?.accept) {
            try {
                inst.off('accept', handlers.accept);
            } catch {
                // Ignore errors
            }
        }

        if (handlers?.complete) {
            try {
                inst.off('complete', handlers.complete);
            } catch {
                // Ignore errors
            }
        }

        // Remove blur handler
        if (handlers?.blur) {
            try {
                element.removeEventListener('blur', handlers.blur);
            } catch {
                // Ignore errors
            }
        }

        // Destroy the IMask instance
        try {
            inst.destroy();
        } catch {
            // Ignore errors
        }
    } catch {
        // Ignore errors during cleanup
    }

    // Cleanup dataset if it matches
    try {
        if ((element as any)?.dataset?.imaskId === id) {
            delete (element as any).dataset.imaskId;
        }
    } catch {
        // Ignore errors
    }

    maskRegistry.delete(id);
    return true;
}

/**
 * Set a value on a mask instance.
 * 
 * @param id - Instance ID
 * @param value - Value to set
 * @param mode - Value mode ('typed', 'unmasked', or 'value')
 * @returns True if value was set successfully
 */
export function setValue(id: string, value: any, mode?: MaskValueMode): boolean {
    const entry = maskRegistry.get(id);
    if (!entry) {
        return false;
    }

    try {
        if (mode === 'typed') {
            entry.inst.typedValue = value;
        } else if (mode === 'unmasked') {
            entry.inst.unmaskedValue = value;
        } else {
            entry.inst.value = value ?? '';
        }
        return true;
    } catch {
        return false;
    }
}

/**
 * Get a value from a mask instance.
 * 
 * @param id - Instance ID
 * @param mode - Value mode ('typed', 'unmasked', or 'value')
 * @returns Value or null if instance not found
 */
export function getValue(id: string, mode?: MaskValueMode): any {
    const entry = maskRegistry.get(id);
    if (!entry) {
        return null;
    }

    try {
        if (mode === 'typed') return entry.inst.typedValue;
        if (mode === 'unmasked') return entry.inst.unmaskedValue;
        return entry.inst.value;
    } catch {
        return null;
    }
}

/**
 * Update mask options on an existing instance.
 * Preserves current value unless mask format changes.
 * 
 * @param id - Instance ID
 * @param config - New mask configuration
 * @returns True if options were updated successfully
 */
export function updateOptions(id: string, config: MaskConfig): boolean {
    const entry = maskRegistry.get(id);
    if (!entry) {
        return false;
    }

    try {
        const options = buildMaskOptions(config || {});
        entry.inst.updateOptions(options);
        return true;
    } catch {
        return false;
    }
}
